import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Clock, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");
export const fmtRemaining = (sec) => `${pad(Math.floor(sec / 60))}:${pad(sec % 60)}`;

// Derive the countdown from the server's timestamps + a server-clock offset, never from local elapsed time.
// States: NOT_STARTED → ACTIVE (paid) → GRACE (system) → EXTRA (lawyer gift, optional) → EXPIRED.
export function deriveTimer(timer, nowMs) {
  if (!timer?.started_at) return { state: "NOT_STARTED", remaining: 0 };
  const offset = timer.server_now ? new Date(timer.server_now).getTime() - (timer.fetched_at ?? nowMs) : 0;
  const now = nowMs + offset;
  const end = new Date(timer.consultation_end_at).getTime();
  const graceEnd = new Date(timer.grace_end_at).getTime();
  const finalEnd = new Date(timer.final_end_at || timer.grace_end_at).getTime();
  if (timer.state === "EXPIRED" || now >= finalEnd) return { state: "EXPIRED", remaining: 0 };
  if (now < end) return { state: "ACTIVE", remaining: Math.ceil((end - now) / 1000) };
  if (now < graceEnd) return { state: "GRACE", remaining: Math.ceil((graceEnd - now) / 1000) };
  return { state: "EXTRA", remaining: Math.ceil((finalEnd - now) / 1000) };
}

// One-second ticker + one-shot notifications (final minute, entering grace) + onExpired (display-only; server decides).
function useCountdown(timer, { notify, onExpired }) {
  const [now, setNow] = useState(Date.now());
  const notified = useRef({ lastMinute: false, grace: false, expired: false });
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const { state, remaining } = deriveTimer(timer, now);
  useEffect(() => {
    if (!notify) return;
    const n = notified.current;
    if (state === "ACTIVE" && remaining <= 60 && remaining > 0 && !n.lastMinute) {
      n.lastMinute = true;
      toast.info("One minute of consultation time left", { duration: 4000 });
    }
    if (state === "GRACE" && !n.grace) {
      n.grace = true;
      toast.warning(`Consultation time has ended — a ${timer?.grace_minutes ?? 5}-minute grace period has started to wrap up.`, { duration: 6000 });
    }
    if (state === "EXPIRED" && !n.expired && timer?.started_at) {
      n.expired = true;
      onExpired?.();
    }
  }, [state, remaining, onExpired, timer?.started_at, timer?.grace_minutes, notify]);
  return { state, remaining };
}

// Lawyer's one-time "extra time" action, offered only during system grace.
export function GiftButton({ timer, onGift, dark = false }) {
  const [busy, setBusy] = useState(false);
  if (!onGift || !timer || timer.gift_granted_at) return null;
  const click = async () => { setBusy(true); try { await onGift(); } finally { setBusy(false); } };
  return (
    <button type="button" onClick={click} disabled={busy} data-testid="gift-time-btn" title="One-time courtesy: extends the final deadline"
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors disabled:opacity-60 ${dark ? "bg-[#D4AF37] text-[#0A2540] hover:bg-[#e2c35a]" : "bg-[#0A2540] text-white hover:bg-[#13365c]"}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Gift size={12} />} Gift {timer.gift_minutes} min
    </button>
  );
}

// Light chip for the chat header (chat consultations): "● LIVE 12:43" / "⚠ Grace 01:42" / "🎁 Extra time 02:31".
export function SessionTimer({ timer, onExpired, onGift, compact = false, notify = !compact, testid = compact ? "session-timer-compact" : "session-timer" }) {
  const { state, remaining } = useCountdown(timer, { notify, onExpired });
  if (state === "NOT_STARTED" || state === "EXPIRED") return null;
  const size = compact ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-[11px]";
  const time = <span className="font-mono" data-testid={`${testid}-remaining`}>{fmtRemaining(remaining)}</span>;
  if (state === "GRACE") {
    return (
      <span className="inline-flex items-center gap-2">
        <span data-testid={testid} data-state="GRACE" className={`inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-bold ${size}`}>
          <AlertTriangle size={compact ? 10 : 12} className="shrink-0" />{!compact && <span>Consultation time ended ·</span>} Grace {time}
        </span>
        {state === "GRACE" && <GiftButton timer={timer} onGift={onGift} />}
      </span>
    );
  }
  if (state === "EXTRA") {
    return (
      <span data-testid={testid} data-state="EXTRA" className={`inline-flex items-center gap-1.5 rounded-full bg-[#FBF4DF] border border-[#D4AF37] text-[#0A2540] font-bold ${size}`}>
        <Gift size={compact ? 10 : 12} className="shrink-0" /> Extra time {time}
      </span>
    );
  }
  const warn = remaining <= 60;
  return (
    <span data-testid={testid} data-state="ACTIVE" className={`inline-flex items-center gap-1.5 rounded-full font-bold ${warn ? "bg-red-50 border border-red-200 text-red-700" : "bg-emerald-50 border border-emerald-200 text-emerald-800"} ${size}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${warn ? "bg-red-500" : "bg-emerald-500"} animate-pulse`} />
      LIVE {time}
    </span>
  );
}

// Dark call-bar variant (audio/video stage): "VIDEO · LIVE · 12:34" / "GRACE · 01:42" / "EXTRA TIME · 02:31" / "SESSION ENDED" / pre-start hint.
export function CallBarTimer({ kind = "Video", timer, onExpired, onGift, canGift = false, notify = true }) {
  const { state, remaining } = useCountdown(timer, { notify, onExpired });
  const label = kind.toUpperCase();
  const time = <span className="font-mono tabular-nums" data-testid="call-bar-timer-remaining">{fmtRemaining(remaining)}</span>;
  if (state === "NOT_STARTED") {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-white/70" data-testid="call-bar-timer" data-state="NOT_STARTED">
        <Clock size={12} /> Timer starts when the lawyer joins the call.
      </span>
    );
  }
  if (state === "EXPIRED") {
    return <span className="text-xs font-bold tracking-wider uppercase text-white/60" data-testid="call-bar-timer" data-state="EXPIRED">{label} · Session ended</span>;
  }
  const styles = {
    ACTIVE: remaining <= 60 ? "bg-red-500/20 text-red-200 border-red-400/40" : "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
    GRACE: "bg-amber-500/20 text-amber-100 border-amber-400/50",
    EXTRA: "bg-[#D4AF37]/25 text-[#F5E6B8] border-[#D4AF37]/60",
  }[state];
  const dot = { ACTIVE: remaining <= 60 ? "bg-red-400" : "bg-emerald-400", GRACE: "bg-amber-400", EXTRA: "bg-[#D4AF37]" }[state];
  const word = { ACTIVE: "LIVE", GRACE: "GRACE", EXTRA: "EXTRA TIME" }[state];
  return (
    <span className="inline-flex items-center gap-2">
      <span data-testid="call-bar-timer" data-state={state} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold tracking-wider ${styles}`}>
        {state === "EXTRA" ? <Gift size={12} /> : state === "GRACE" ? <AlertTriangle size={12} /> : <span className={`h-1.5 w-1.5 rounded-full ${dot} animate-pulse`} />}
        <span className="hidden sm:inline">{label} ·</span> {word} · {time}
      </span>
      {state === "GRACE" && canGift && <GiftButton timer={timer} onGift={onGift} dark />}
    </span>
  );
}

// Hint shown before the timer starts.
// Client-side display of the server's lawyer waiting period (scheduled audio/video, before the lawyer joins).
// Counts down to `deadline_utc` using the server-clock offset; at zero it only asks the server (onExpired) — never decides.
export function deriveWait(ns, nowMs) {
  if (!ns?.deadline_utc) return { waiting: false, remaining: 0, beforeSlot: false };
  const offset = ns.server_now ? new Date(ns.server_now).getTime() - (ns.fetched_at ?? nowMs) : 0;
  const now = nowMs + offset;
  const start = ns.slot_start_utc ? new Date(ns.slot_start_utc).getTime() : 0;
  const deadline = new Date(ns.deadline_utc).getTime();
  const beforeSlot = ns.stage !== "initiation" && now < start;
  return { waiting: !beforeSlot && now < deadline, beforeSlot, remaining: Math.max(0, Math.ceil((deadline - now) / 1000)) };
}

export function LawyerWaitCountdown({ noShow, onExpired, isLawyerSide = false }) {
  const [now, setNow] = useState(Date.now());
  const fired = useRef(false);
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const { waiting, beforeSlot, remaining } = deriveWait(noShow, now);
  useEffect(() => {
    if (!noShow?.applies || beforeSlot || remaining > 0 || fired.current) return;
    fired.current = true;
    onExpired?.();
  }, [noShow?.applies, beforeSlot, remaining, onExpired]);
  if (!noShow?.applies || beforeSlot) return null;
  const initiation = noShow.stage === "initiation";
  return (
    <div className="mt-6 rounded-xl border border-amber-300/40 bg-amber-400/10 px-4 py-3 text-left" data-testid="lawyer-wait-panel">
      <p className="text-sm font-bold text-white inline-flex items-center gap-2">
        <Loader2 size={14} className="animate-spin text-amber-300" /> {initiation
          ? "Consultation initiation window"
          : isLawyerSide ? "Time to start the consultation" : "Waiting for the lawyer to start…"}
      </p>
      <p className="mt-1 text-xs text-white/70">
        {initiation
          ? `The lawyer must ${noShow.consultation_type === "chat" ? "send the first message" : "join the call"} within ${noShow.initiation_grace_minutes || 2} minutes after starting. If not, this booking closes and is refunded automatically.`
          : "If the lawyer doesn't start within the countdown, this booking closes and is refunded automatically."}
      </p>
      <p className="mt-2 text-xs font-bold tabular-nums text-amber-200 inline-flex items-center gap-1" data-testid="lawyer-wait-countdown" data-remaining={remaining} data-stage={noShow.stage}>
        <Clock size={12} /> {waiting ? `${initiation ? "Initiation window" : "Waiting for lawyer"} · ${fmtRemaining(remaining)}` : "Checking with the server…"}
      </p>
    </div>
  );
}

export function TimerStartHint({ consultationType, isLawyerSide }) {
  const chat = consultationType === "chat";
  const text = chat
    ? (isLawyerSide ? "Your first message will start the consultation timer." : "The timer starts when the lawyer sends the first message.")
    : (isLawyerSide ? "The timer starts when you join the call." : "Timer starts when the lawyer joins the call.");
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" data-testid="timer-start-hint">
      <Clock size={11} /> {text}
    </span>
  );
}
